import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { certificatesApi } from '../utils/api';
import { queryKeys } from '../hooks/useData';
import { useToast } from '../contexts/ToastContext';
import MultiStepWizardPage from '../components/Wizard/MultiStepWizardPage';
import CertificateTemplateMetaStep from '../components/Modal/steps/CertificateTemplateMetaStep';
import CertificateTemplateUploadStep from '../components/Modal/steps/CertificateTemplateUploadStep';
import CertificateTemplateSchemaPreviewStep from '../components/Modal/steps/CertificateTemplateSchemaPreviewStep';
import CertificateTemplateLowConfidenceReviewStep from '../components/Modal/steps/CertificateTemplateLowConfidenceReviewStep';
import CertificateTemplateConfirmStep from '../components/Modal/steps/CertificateTemplateConfirmStep';
import { inferRequestFlowTemplateKeyFromWizardContext } from '../constants/requestCertificateTemplates';

export default function CertificateTemplateWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const cancel = () => {
    navigate('/dashboard/settings?tab=documents');
  };

  return (
    <div className="page-container">
      <MultiStepWizardPage
        title="Create certificate template"
        subtitle="Upload a certificate PDF. We parse layout (zones, tables, checklists, widgets) and generate a multi-screen form automatically."
        onCancel={cancel}
        onComplete={async (data: any) => {
          const certificatePrefix = String(data.certificatePrefix || '').trim();
          const certificateTypeName = String(data.certificateTypeName || '').trim();
          const templateLabel = String(data.templateLabel || '').trim();
          const pdfStoragePath = String(data.pdfStoragePath || '').trim();
          const importId = String(data.importId || '').trim();
          const inferredSchema = (data as any)?.inferredSchema;
          const edits: Record<string, any> =
            (data as any)?.lowConfidenceEdits && typeof (data as any).lowConfidenceEdits === 'object'
              ? (data as any).lowConfidenceEdits
              : {};

          const baseSchema = (data as any)?.baseInferredSchema || inferredSchema;

          const findFieldLocation = (schemaJson: any, fieldSignature: string) => {
            const screens: any[] = Array.isArray(schemaJson?.screens) ? schemaJson.screens : [];
            for (const s of screens) {
              const screenKey = String(s?.key || '').trim();
              const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
              for (const sec of sections) {
                const sectionKey = String(sec?.key || '').trim();
                const fs: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
                for (const f of fs) {
                  const sig = String(f?.fieldSignature || '').trim();
                  if (sig && sig === fieldSignature) {
                    return {
                      screenKey,
                      sectionKey,
                      semanticTag: String(f?.semanticTag || '').trim(),
                    };
                  }
                }
              }
            }
            return null;
          };

          const corrections = Object.entries(edits)
            .map(([fieldSignature, raw]) => {
              const sig = String(fieldSignature || '').trim();
              if (!sig) return null;

              const baseLoc = findFieldLocation(baseSchema, sig);
              const fromSemanticTag = String(baseLoc?.semanticTag || '').trim() || undefined;
              const fromScreenKey = String(baseLoc?.screenKey || '').trim() || undefined;
              const fromSectionKey = String(baseLoc?.sectionKey || '').trim() || undefined;

              const obj = typeof raw === 'string' ? { toSemanticTag: raw } : (raw || {});
              const toSemanticTag = obj?.toSemanticTag ? String(obj.toSemanticTag).trim() : '';
              const toScreenKey = obj?.toScreenKey ? String(obj.toScreenKey).trim() : '';
              const toSectionKey = obj?.toSectionKey ? String(obj.toSectionKey).trim() : '';

              const hasTo = Boolean(toSemanticTag || toScreenKey || toSectionKey);
              if (!hasTo) return null;

              const semanticChanged = Boolean(toSemanticTag && toSemanticTag !== (fromSemanticTag || ''));
              const screenChanged = Boolean(toScreenKey && toScreenKey !== (fromScreenKey || ''));
              const sectionChanged = Boolean(toSectionKey && toSectionKey !== (fromSectionKey || ''));
              if (!semanticChanged && !screenChanged && !sectionChanged) return null;

              const out: any = {
                fieldSignature: sig,
                fromSemanticTag,
                toSemanticTag: toSemanticTag || fromSemanticTag || 'unknown',
                fromScreenKey,
                toScreenKey: toScreenKey || undefined,
                fromSectionKey,
                toSectionKey: toSectionKey || undefined,
              };

              return out;
            })
            .filter(Boolean);

          if (!certificatePrefix || !certificateTypeName || !templateLabel || !pdfStoragePath || !importId) {
            throw new Error('Missing template details');
          }

          if (!inferredSchema || typeof inferredSchema !== 'object') {
            throw new Error('No schema generated');
          }

          const requestFlowKey = inferRequestFlowTemplateKeyFromWizardContext({
            originalPdfFileName: (data as any)?.templateFile?.name,
            certificatePrefix,
            certificateTypeName,
            templateLabel,
          });
          const schemaJson =
            requestFlowKey && inferredSchema && typeof inferredSchema === 'object'
              ? { ...(inferredSchema as object), requestFlowTemplateKey: requestFlowKey }
              : inferredSchema;

          await certificatesApi.confirmTemplateSchema({
            importId,
            certificatePrefix,
            certificateTypeName,
            templateLabel,
            pdfStoragePath,
            schemaJson,
            corrections,
          });

          await queryClient.invalidateQueries({ queryKey: queryKeys.certificateTemplates });
          showToast('Certificate template saved', 'success');
          cancel();
        }}
        steps={[
          {
            id: 'meta',
            title: 'Details',
            component: CertificateTemplateMetaStep,
            validation: (d) => {
              if (!String(d.certificatePrefix || '').trim()) return false;
              if (!String(d.certificateTypeName || '').trim()) return false;
              if (!String(d.templateLabel || '').trim()) return false;
              return true;
            },
          },
          {
            id: 'upload',
            title: 'Upload & Extract',
            component: CertificateTemplateUploadStep,
            validation: (d) => {
              if (!d.templateFile) return false;
              if (!String(d.pdfStoragePath || '').trim()) return false;
              return Boolean(String(d.importId || '').trim() && d.inferredSchema);
            },
          },
          {
            id: 'schema',
            title: 'Form Preview',
            component: CertificateTemplateSchemaPreviewStep,
            validation: (d) => Boolean(String(d.importId || '').trim() && d.inferredSchema),
          },
          {
            id: 'review',
            title: 'Review',
            component: CertificateTemplateLowConfidenceReviewStep,
            validation: (d) => Boolean(String(d.importId || '').trim() && d.inferredSchema),
          },
          {
            id: 'confirm',
            title: 'Confirm',
            component: CertificateTemplateConfirmStep,
          },
        ]}
      />
    </div>
  );
}
